import { Component, inject, OnInit, signal } from '@angular/core';
import { Router, RouterLink } from '@angular/router';
import { IInvoiceListsDateRange, InvoiceStatus, ISaleStatus, InvoicingService } from '../../../core/services/invoicing.service';
import { MerchantsService } from '../../../core/services/merchants.service';
import { StoresService } from '../../../core/services/stores.service';
import { ToolsService } from '../../../core/services/tools.service';
import { UserService } from '../../../core/services/user.service';
import { FormBuilder, FormGroup, ReactiveFormsModule } from '@angular/forms';
import { format } from 'date-fns';
import { toSignal } from '@angular/core/rxjs-interop';
import { debounceTime, Subject } from 'rxjs';
import { BsModalService } from 'ngx-bootstrap/modal';
import { SaleDetailsComponent } from '../sale-details/sale-details.component';
import { ApiGatewayService } from '../../../core/services/api-gateway.service';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { TextInputComponent } from '../../../shared-with-client/text-input/text-input.component';
import { LoaderComponent } from '../../../shared-with-client/loader/loader.component';
import { DatePipe, JsonPipe } from '@angular/common';
import { SalesStatusComponent } from '../sales-status/sales-status.component';
import { BsDropdownDirective, BsDropdownMenuDirective, BsDropdownToggleDirective } from 'ngx-bootstrap/dropdown';
import { MerchantCurrencyPipe } from '../../../shared/pipes/merchant-currency.pipe';
import { DateRangeModalComponent } from '../../../shared/date-range-modal/date-range-modal.component';

@Component({
    selector: 'app-sales-list-page',
    imports: [
        TranslatePipe,
        ReactiveFormsModule,
        TextInputComponent,
        LoaderComponent,
        DatePipe,
        SalesStatusComponent,
        RouterLink,
        BsDropdownDirective,
        BsDropdownToggleDirective,
        BsDropdownMenuDirective,
        MerchantCurrencyPipe,
    ],
    templateUrl: './sales-list-page.component.html',
    styleUrl: './sales-list-page.component.scss',
})
export class SalesListPageComponent implements OnInit {
    private merchantsService = inject(MerchantsService);
    private toolsService = inject(ToolsService);
    private storesService = inject(StoresService);
    private userService = inject(UserService);
    private bsModalService = inject(BsModalService);
    private translateService = inject(TranslateService);
    private api = inject(ApiGatewayService);

    private invoicingService = inject(InvoicingService);
    private router = inject(Router);
    private fb = inject(FormBuilder);

    public currentMerchant = toSignal(this.merchantsService.activeMerchant);
    public currentStore = toSignal(this.storesService.getActiveStore());
    public currentUser = toSignal(this.userService.currentUser);

    public loading = signal(true);
    public availableUsers = signal<any[]>([]);

    public salesData = signal<{ summary: any; sales: any[] }>({ summary: [], sales: [] });

    public searchForm!: FormGroup;
    public filterDateRanges = this.invoicingService.dateRanges;
    public selectedDateRange = signal(this.filterDateRanges[4]); // Este mês

    public saleStatuses: any = [...this.invoicingService.saleStatuses];

    public triggerSearchObservable = new Subject<boolean>();

    public isInvoicingActive = toSignal(this.storesService.isInvoicingActive());

    ngOnInit(): void {
        this.setupForm();

        this.loadData();

        this.triggerSearchObservable.pipe(debounceTime(50)).subscribe(() => {
            this.loadData();
        });

        if (this.isInvoicingActive()) {
            this.saleStatuses.push({ label: this.translateService.instant('SALES_STATUS.uninvoiced'), value: InvoiceStatus.UNINVOICED });
        }
    }

    setupForm(): void {
        const parsedDateRange = this.invoicingService.parseDateRange(this.selectedDateRange().value);

        this.searchForm = this.fb.group({
            search: [''],
            status: [null],
            createdByUser: [null],
            startDate: [parsedDateRange?.startDate],
            endDate: [parsedDateRange?.endDate],
        });

        this.storesService.getStoreUsers(this.currentStore().id).subscribe((users) => {
            this.availableUsers.set(users);
        });

        this.searchForm
            .get('search')!
            .valueChanges.pipe(debounceTime(500))
            .subscribe(() => {
                // Handle search changes
                this.loadData();
            });
        const otherFields = ['status', 'createdByUser', 'startDate', 'endDate'];
        otherFields.forEach((field) => {
            this.searchForm.get(field)!.valueChanges.subscribe(() => {
                // Handle other field changes
                this.triggerSearchObservable.next(true);
            });
        });
    }

    loadData(): void {
        this.loading.set(true);
        this.invoicingService
            .getSales({
                storeId: this.currentStore().id,
                status: (this.searchForm.get('status')?.value?.value ?? '') as string,
                search: (this.searchForm.get('search')?.value ?? '') as string,
                createdByUserId: this.searchForm.get('createdByUser')?.value?.id ?? null,
                startDate: this.searchForm.get('startDate')?.value ?? null,
                endDate: this.searchForm.get('endDate')?.value ?? null,
            })
            .subscribe({
                next: (response) => {
                    const sales = (response?.sales ?? []).map((sale: any) => ({
                        ...sale,
                        lineKind: 'sale',
                        sortAt: sale.createdAt,
                    }));
                    const cancellations = (response?.cancellations ?? []).map((sale: any) => ({
                        ...sale,
                        lineKind: 'cancellation',
                        sortAt: sale.cancelledAt,
                    }));
                    const rows = [...sales, ...cancellations].sort(
                        (a, b) => new Date(b.sortAt).getTime() - new Date(a.sortAt).getTime(),
                    );

                    this.salesData.set({
                        summary: response?.summary,
                        sales: rows,
                    });
                    this.loading.set(false);
                },
                error: () => {
                    // In case of error, assume there's no overlap
                    this.loading.set(false);
                },
            });
    }

    setDateRange(range: any) {
        if (range.value === IInvoiceListsDateRange.CUSTOM) {
            this.openCustomRange(range);
        } else {
            this.selectedDateRange.set(range);
            const parsedRange = this.invoicingService.parseDateRange(range.value);
            this.searchForm.patchValue({
                startDate: parsedRange?.startDate,
                endDate: parsedRange?.endDate,
            });
        }
    }

    setSaleStatuses(status: any) {
        this.searchForm.patchValue({
            status: status,
        });
    }

    setCreatedByUser(user: any) {
        this.searchForm.patchValue({
            createdByUser: user,
        });
    }

    openCustomRange(range: any) {
        const initialState = {
            data: {
                dateInit: null,
                dateEnd: null,
            },
        };

        this.bsModalService.show(DateRangeModalComponent, { initialState, class: 'modal-md' });

        const modalCallback = this.bsModalService.onHide.subscribe((reason: string) => {
            if (reason && typeof reason === 'object') {
                const { dateInit, dateEnd } = reason as { dateInit: Date; dateEnd: Date };
                if (dateInit && dateEnd) {
                    this.searchForm.patchValue({
                        startDate: format(dateInit, 'yyyy-MM-dd'),
                        endDate: format(dateEnd, 'yyyy-MM-dd'),
                    });
                }
                this.selectedDateRange.set(range);
            }
            modalCallback.unsubscribe();
        });
    }

    newPayment(): void {
        this.router.navigateByUrl('/' + this.currentStore().slug + '/dashboard/payment/create');
    }

    downloadAll(): void {
        this.invoicingService
            .downloadAllSales({
                storeId: this.currentStore().id,
                status: (this.searchForm.get('status')?.value?.value ?? '') as string,
                search: (this.searchForm.get('search')?.value ?? '') as string,
                createdByUserId: this.searchForm.get('createdByUser')?.value?.id ?? null,
                startDate: this.searchForm.get('startDate')?.value ?? null,
                endDate: this.searchForm.get('endDate')?.value ?? null,
            })
            .subscribe({
                next: (blob) => {
                    const dateStr = format(new Date(), 'yyyyMMdd-HHmmss');
                    const filename = `sales-invoices-${dateStr}.zip`;
                    this.toolsService.downloadBlobAsFile(blob, filename);
                },
            });
    }

    exportXLS(): void {
        this.invoicingService
            .downloadAllSalesXLS({
                storeId: this.currentStore().id,
                status: (this.searchForm.get('status')?.value?.value ?? '') as string,
                search: (this.searchForm.get('search')?.value ?? '') as string,
                createdByUserId: this.searchForm.get('createdByUser')?.value?.id ?? null,
                startDate: this.searchForm.get('startDate')?.value ?? null,
                endDate: this.searchForm.get('endDate')?.value ?? null,
            })
            .subscribe({
                next: (blob) => {
                    // console.log('blob:', blob);
                    const dateStr = format(new Date(), 'yyyyMMdd-HHmmss');
                    const filename = `${this.translateService.instant('GENERAL.salesExport')}-${dateStr}.xlsx`;
                    this.toolsService.downloadBlobAsFile(blob, filename);
                },
            });
    }

    displaySale(line: any): any {
        if (line?.lineKind === 'cancellation') {
            return { ...line, status: ISaleStatus.CANCELLED };
        }

        if (line?.status !== ISaleStatus.CANCELLED) {
            return line;
        }

        const paid = line.paidAmountCents ?? 0;
        const total = line.totalCents ?? 0;
        const hadInvoice = !!(line.invoiceReference || line.saleInvoices?.length || line.creditNotes?.length);

        return {
            ...line,
            status: paid >= total ? ISaleStatus.PAID : ISaleStatus.PENDING_PAYMENT,
            invoiceStatus: hadInvoice ? InvoiceStatus.INVOICED : line.invoiceStatus,
        };
    }

    seeDetails(sale: any): void {
        this.bsModalService.show(SaleDetailsComponent, { initialState: { sale }, class: 'modal-lg' });
        const modalCallback = this.bsModalService.onHide.subscribe((reason: string) => {
            if (reason && reason === 'updated') {
                this.loadData();
            }
            modalCallback.unsubscribe();
        });
    }

    emailInvoice(sale: any): void {
        this.invoicingService.requestEmailInvoice({
            merchantClientEmail: sale.merchantClient?.email,
            saleId: sale.id,
            saleInvoiceId: sale?.saleInvoices?.[0]?.id,
        });
    }

    downloadInvoice(line: any): void {
        this.api.downloadUrlAsFile(line.invoicePdfUrl, `buk_${line.invoiceReference}.pdf`);
    }
}
